import { useMatomo } from '@datapunt/matomo-tracker-react';
import { Box, CircularProgress, Grid, TextField } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';

import { CmsArticles, fetchCmsArticle } from '../../../../shared/helpers/cms-articles';
import { deleteSignatureAsync, saveSignatureAsync } from '../../../redux/scholarship.actions';
import { EnumAppSignatureFormat, EnumAppSignatureType, MAppSignature } from '../../../redux/scholarship.models';
import { selectIsSavingPurchaser } from '../../../redux/scholarship.selectors';
import { ApplicationFormContainer } from '../../ApplicationsForm/applications-form.styles';

interface IApplicationsFormTtpfPurchaserProps {
  id: string;
  signature: MAppSignature;
  handleToggle: (status: boolean) => void;
  dispatchPurchaserSave: (id: string, value: unknown) => Promise<boolean>;
  dispatchPurchaserDelete: (id: string, value: MAppSignature) => Promise<boolean>;
}

const useStyles = makeStyles((theme) =>
  createStyles({
    cardTop: {
      marginTop: theme.spacing(3)
    },
    formControlLabel: {
      fontSize: '0.9rem'
    },
    radio: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      alignItems: 'flex-start'
    },
    button: {
      height: '45px',
      marginLeft: '15px',
      marginRight: '15px'
    },
    buttons: {
      width: '100%',
      textAlign: 'center'
    },
    buttonSmall: {
      marginRight: theme.spacing(1),
      height: '25px',
      font: '15px;',
      backgroundColor: '#f87c7c'
    },
    rootLeft: {
      width: '100%',
      textAlign: 'right',
      marginTop: '40px'
    },
    topLeftBox: {
      justifyContent: 'flex-start',
      alignItems: 'flex-start'
    }
  })
);

const ApplicationsFormTtpfPurchaser = ({
  id,
  signature,
  handleToggle,
  dispatchPurchaserSave,
  dispatchPurchaserDelete
}: IApplicationsFormTtpfPurchaserProps) => {
  const classes = useStyles();
  const savingPurchaser = useSelector(selectIsSavingPurchaser as (state: unknown) => boolean);
  const [firstName, setFirstName] = useState<string>(signature.firstName || '');
  const [lastName, setLastName] = useState<string>(signature.lastName || '');
  const [email, setEmail] = useState<string>(signature.email || '');
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [customEmailBody, setCustomEmailBody] = useState<string>(signature.customEmailBody || '');
  const [signatureFormat, setSignatureFormat] = useState<EnumAppSignatureFormat>(
    signature.signatureFormat || EnumAppSignatureFormat.DIGITAL
  );
  const [purchaserCertification, setPurchaserCertification] = useState('');
  const [purchaserLegalText, setPurchaserLegalText] = useState('');
  const { trackPageView } = useMatomo();

  // Track page view
  useEffect(() => {
    trackPageView({});
  }, []);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const nonPurchaserCertificationHTML = await fetchCmsArticle(CmsArticles.PURCHASER_CERTIFICATION);
        const purchaserLegalTextHTML = await fetchCmsArticle(CmsArticles.PURCHASER_LEGAL_TEXT);
        setPurchaserCertification(nonPurchaserCertificationHTML);
        setPurchaserLegalText(purchaserLegalTextHTML);
        localStorage.setItem('nonPurchaserCertification', purchaserCertification || '');
      } catch (e) {
        console.error(e);
      }
    };

    void fetchArticle();
  }, []);

  const handleSubmit = (): void => {
    void dispatchPurchaserSave(id, {
      signatureId: signature.signatureId || undefined,
      firstName,
      lastName,
      email,
      customEmailBody,
      signatureFormat,
      signatureType: EnumAppSignatureType.PURCHASER,
      signed: false,
      signedAgreementWording: purchaserLegalText
    }).then((result) => {
      if (result) {
        handleToggle(false);
      }
    });
  };

  const handleConfirmType = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setConfirmation(e.target.value.toLowerCase());
  };

  const handleConfirmDelete = (): void => {
    setOpen(false);
    if (confirmation === 'delete') {
      void dispatchPurchaserDelete(id, signature).then((result) => {
        if (result) {
          handleToggle(false);
        }
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDelete = () => {
    setOpen(true);
  };

  return (
    <ApplicationFormContainer>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="body1">
            Please choose one of the following options to obtain the consent of the purchaser of your child’s Texas Tuition Promise Fund
            contract for scholarship participation.
          </Typography>
        </Grid>
      </Grid>
      <Box ml={4} mr={4} mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box mt={3} className={classes.topLeftBox}>
              <RadioGroup
                aria-label="relationship"
                name="relationship"
                value={signatureFormat}
                onChange={(e) => setSignatureFormat(e.target.value as EnumAppSignatureFormat)}
              >
                <FormControlLabel
                  key={EnumAppSignatureFormat.DIGITAL}
                  value={EnumAppSignatureFormat.DIGITAL}
                  control={<Radio />}
                  className={classes.radio}
                  label={
                    <Typography className={classes.formControlLabel}>
                      I will provide an email address to obtain this person’s signature digitally. I understand they will receive an email
                      from this site saying their signature has been requested for a Match the Promise Foundation scholarship application on
                      behalf of my child and to contact me if any questions. I understand I will be copied on this email. The email will
                      provide a link for the person to acknowledge their consent for scholarship participation. I can also provide a custom
                      message below to include with the email.
                    </Typography>
                  }
                />

                <FormControlLabel
                  key={EnumAppSignatureFormat.PAPER}
                  value={EnumAppSignatureFormat.PAPER}
                  control={<Radio />}
                  className={classes.radio}
                  label={
                    <>
                      <Typography className={classes.formControlLabel}>
                        I will print a paper consent to obtain this person’s signature myself. Once signed, I will upload a picture of the
                        signed consent to this site to complete the process.{' '}
                      </Typography>
                    </>
                  }
                />
              </RadioGroup>
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              required
              id="firstName"
              name="firstName"
              label="Purchaser First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              required
              id="lastName"
              name="lastName"
              label="Purchaser Last Name"
              fullWidth
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Grid>
          {signatureFormat === EnumAppSignatureFormat.DIGITAL && (
            <Grid item xs={12} sm={4}>
              <TextField
                required
                id="email"
                name="email"
                label="Purchaser Email Address"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Grid>
          )}
        </Grid>
        {signatureFormat === EnumAppSignatureFormat.DIGITAL && (
          <Box mt={3}>
            <Grid item xs={12} style={{ marginBottom: '10px' }}>
              <Typography variant="body2">Custom message to be included in the signature request email.</Typography>
            </Grid>

            <TextField
              id="customEmailBody"
              label="Email Message"
              multiline
              rows={4}
              fullWidth
              variant={'outlined'}
              value={customEmailBody}
              onChange={(e) => setCustomEmailBody(e.target.value)}
            />
          </Box>
        )}
      </Box>

      <Grid container justifyContent="space-between">
        <Grid item xs={12}>
          {signatureFormat === EnumAppSignatureFormat.PAPER && (
            <Box m={3}>
              <Typography variant="body2">
                After adding this purchaser, use the Download Form button to download the PDF you will need for the signature.{' '}
              </Typography>
            </Box>
          )}
          {signatureFormat === EnumAppSignatureFormat.DIGITAL && (
            <Box m={3}>
              <Typography variant="body2">
                If you chose to obtain the consent of the purchaser digitally, saving this page will automatically generate an email to that
                person.
              </Typography>
            </Box>
          )}
          <div className={classes.buttons}>
            <Button variant="contained" className={classes.button} onClick={() => handleToggle(false)}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" className={classes.button} onClick={() => handleSubmit()}>
              Save and Continue
              {savingPurchaser && (
                <Box ml={2} mt={1}>
                  <CircularProgress size={18} color={'inherit'} />
                </Box>
              )}
            </Button>
          </div>
        </Grid>
      </Grid>

      {signature.signatureId && (
        <div className={classes.rootLeft}>
          <Button variant="contained" color="secondary" onClick={handleDelete} className={classes.buttonSmall}>
            Delete Purchaser
          </Button>
        </div>
      )}

      <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            To delete this purchaser, type in the word <b>delete</b> then click Submit.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="confirm"
            label="type 'delete' to confirm this action"
            type="text"
            fullWidth
            onChange={handleConfirmType}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </ApplicationFormContainer>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchPurchaserSave: (id: string, value: unknown): Promise<boolean> => {
    return dispatch(saveSignatureAsync(id, value)) as Promise<boolean>;
  },
  dispatchPurchaserDelete: (id: string, value: MAppSignature): Promise<boolean> => {
    return dispatch(deleteSignatureAsync(id, value)) as Promise<boolean>;
  }
});

export default connect(null, mapDispatchToProps)(ApplicationsFormTtpfPurchaser);
